import React, {useEffect, useState} from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {menuItemsInfo} from "../editMenuItems";
import {NodeType} from "../../../../../model/ModelData";
import {v4 as uuidv4} from "uuid";
import {HugeiconsIcon} from "@hugeicons/react";
import {Add01Icon, PencilEdit01Icon, Settings01Icon} from "@hugeicons/core-free-icons";
import {CreateDynamicRouteVarModal} from "./CreateDynamicRouteVarModal";
import {CustomAutocomplete} from "../../../../../components/inputs/CustomAutocomplete";
import MultiSelectAutocomplete from "../../../../../components/inputs/multi-select-autocomplete";
import TagManagementModal from "../components/tag-management-modal";
import {VariableTextField} from "../components/VariableTextField";
import {UI, rgba} from "../../uiTokens";
import {
  checkboxSx,
  controlLabelSx,
  fieldSx,
  iconBtnSx,
  RouteSection,
  sectionLabelSx,
} from "./routeStyles";
import {RouteParamsList} from "./RouteParamsList";

export const DynamicRoutesTab = ({node, setNode, nodes, onUpdate, project, flow, selectedRoute}) => {
  const [intents, setIntents] = useState([]);
  const [open, setOpen] = useState(null);
  const [selectedVar, setSelectedVar] = useState(null);
  const [manageTagsOpen, setManageTagsOpen] = useState(false);

  useEffect(() => {
    if (project && flow) {
      setIntents(project?.intents || []);
    }
  }, [project, flow]);

  const createRoute = () => {
    return {
      id: uuidv4(),
      target: null,
      name: null,
      type: node?.type === NodeType.SWITCH ? "expression" : "intent",
      backstack: false,
      data: {
        greet: "",
        value: "",
        first_command: "",
        priority: 0,
        tags: [],
      },
    };
  };

  const addDynamicRoute = () => {
    const newNode = {...node};
    if (!newNode.routes.dynamic) {
      newNode.routes.dynamic = [];
    }
    newNode.routes.dynamic.push({
      type: node?.type === NodeType.SWITCH ? "expression" : "intent",
      target: null,
      id: uuidv4(),
      data: {
        intent_id: null,
        intent_name: null,
        greet: "",
        value: "",
        first_command: "",
        is_enabled_cbw: false,
        conversation_buffer_window: 0,
      },
    });

    setNode(newNode);
  };

  const onDeleteRoute = async (routeId) => {
    const newNode = {...node};
    newNode.routes.dynamic = newNode.routes.dynamic?.filter(r => r.id !== routeId);
    setNode(newNode);
    await onUpdate(newNode);
  };

  const routes = node?.routes?.dynamic || [];
  const variables = project?.flow_variables?.[flow?.id] || [];

  let items = nodes?.filter(item => (item?.data?.form_id && item?.type === NodeType.START_FORM) || item);
  items = items?.map(node => ({...node, groupName: `Flow: ${flow?.name}`}));
  if (node?.data?.form_id) {
    const formNode = items?.find(item => item?.data?.form_id === node?.data?.form_id && item?.type === NodeType.START_FORM);
    items = items?.filter(item => item?.data?.form_id === node?.data?.form_id)?.map(item => ({
      ...item,
      groupName: `Form: ${formNode?.name || flow?.name}`,
    }));
  }
  if (node?.type === NodeType?.START) {
    project?.flows?.forEach(f => {
      const startNode = {...f?.nodes?.find(n => n?.type === NodeType.START)};
      if (startNode.id === node.id) return null;

      items?.push({...startNode, groupName: `Flow: ${f?.name}`});
    });
  }
  items = items.sort((a, b) => -b.groupName.localeCompare(a.groupName));

  const handleClose = () => {
    setOpen(null);
    setSelectedVar(null);
  };

  const {primary, secondary} = menuItemsInfo.dynamic;
  const varsInfo = menuItemsInfo.vars || {};

  const groupPopover = {
    paper: {
      sx: {
        mt: 0.5,
        borderRadius: "10px",
        border: `1px solid ${rgba(UI.accent, 0.16)}`,
        boxShadow: UI.shadow,
      },
    },
  };

  const renderGroup = (params) => (
    <li key={params.key}>
      <Typography
        sx={{
          ...sectionLabelSx,
          px: 1.25,
          py: 0.75,
          position: "sticky",
          top: 0,
          zIndex: 1,
          backgroundColor: UI.surface,
        }}
      >
        {params.group}
      </Typography>
      <Box sx={{px: 0.5}}>{params.children}</Box>
    </li>
  );

  return (
    <>
      <CreateDynamicRouteVarModal
        node={node} project={project} handleClose={handleClose}
        open={!!open} onUpdateNode={onUpdate} selectedRoute={open}
        selectedVar={selectedVar}
      />
      <TagManagementModal
        open={manageTagsOpen}
        handleClose={() => setManageTagsOpen(false)}
        tags={project?.settings?.tags || []}
        project={project}
      />

      <Box>
        <Stack direction="row" gap={1} alignItems="center">
          <Box sx={{flex: 1}}>
            <Typography sx={{fontWeight: 700, fontSize: 14, color: UI.ink}}>{primary}</Typography>
            <Typography sx={{mt: 0.25, fontSize: 12, color: UI.muted, lineHeight: 1.45}}>{secondary}</Typography>
          </Box>
          <Button
            size="small"
            startIcon={<HugeiconsIcon icon={Add01Icon} size={15}/>}
            onClick={addDynamicRoute}
            sx={{
              flexShrink: 0,
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "8px",
              color: UI.accent,
              "&:hover": {backgroundColor: rgba(UI.accent, 0.1)},
            }}
          >
            Add route
          </Button>
        </Stack>
      </Box>
      <Stack
          spacing={1}
          sx={{
            overflow: "visible",
            pr: 0.5,
          }}
      >

      {routes.length === 0 && (
        <Typography sx={{fontSize: 12, color: rgba(UI.muted, 0.85)}}>No dynamic routes yet.</Typography>
      )}

      {routes.filter(i => i !== null).map((route, index) => {
        const selectedNode = items?.find(n => n?.id === route?.target);
        const isConnected = !!selectedNode;
        const routeData = node?.routes?.dynamic[index] || {};

        return (
          <RouteSection
            key={route?.id}
            title={route?.data?.intent_name?.replaceAll("_", " ") || `Route ${index + 1}`}
            description="This route will only be executed if intent is catched"
            connected={isConnected}
            connectedLabel={selectedNode?.name}
            defaultExpanded={route?.id === selectedRoute}
            TransitionProps={{
              unmountOnExit: true
            }}
          >
            {node?.type === NodeType.SWITCH ? (
              <VariableTextField
                label="Expression"
                size="small"
                variables={variables}
                fullWidth multiline={true}
                value={route?.data?.value || ""}
                helperText="Write here python expression"
                InputLabelProps={{shrink: true}}
                onBlur={() => onUpdate(node)}
                onChange={(value) => {
                  setNode(prev => {
                    const newNode = {
                      ...prev,
                      routes: {
                        ...prev.routes,
                        dynamic: [...(prev.routes?.dynamic || [])],
                      },
                    };

                    let next = newNode.routes.dynamic[index]
                      ? {...newNode.routes.dynamic[index]}
                      : createRoute();

                    const data = {
                      ...next.data,
                      value: value,
                    };

                    next = {
                      ...next,
                      id: next?.id || uuidv4(),
                      data,
                    };

                    newNode.routes.dynamic[index] = next;

                    return newNode;
                  });
                }}
              />
            ) : (
              <CustomAutocomplete
                id={`${route?.id}-select`}
                label={`Intent [${index}]`}
                items={intents}
                value={route?.data?.intent_id}
                onUpdate={(selectedIntent) => {
                  setNode(prev => {
                    const newNode = {...prev};
                    const find = newNode?.routes.dynamic[index];
                    let next = {...find};
                    if (!find) {
                      next = createRoute();
                    }
                    next.id = next?.id || uuidv4();

                    next.data = {
                      ...next.data,
                      intent_id: selectedIntent?.id,
                      intent_name: selectedIntent?.name,
                    };
                    newNode.routes.dynamic[index] = next;

                    return newNode;
                  });
                }}
              />
            )}

            <Autocomplete
              id={`${route?.id}-select`}
              size="small"
              options={items}
              groupBy={option => option?.groupName}
              getOptionLabel={option => option?.name || ""}
              isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
              value={selectedNode || null}
              blurOnSelect
              slotProps={groupPopover}
              renderInput={(props) => <TextField {...props} size="small" label="Target node" sx={fieldSx}/>}
              renderGroup={renderGroup}
              onChange={(e, value) => {
                const newNode = {...node};
                let selectedNode = value;

                if (selectedNode) {
                  const find = newNode?.routes?.dynamic[index];
                  let next = {...find};
                  if (!next) {
                    next = createRoute();
                  }
                  next.id = next?.id || uuidv4();
                  next.target = selectedNode.id;
                  next.name = selectedNode?.name;
                  newNode.routes.dynamic[index] = next;
                } else {
                  const find = newNode?.routes.dynamic[index];
                  let next = {...find};
                  if (!next) {
                    next = createRoute();
                  }
                  next.id = next?.id || uuidv4();
                  next.target = null;
                  next.name = "";
                  newNode.routes.dynamic[index] = next;
                }
                onUpdate(newNode);
              }}
            />

            <Box>
              <Typography sx={{...sectionLabelSx, mb: 0.5}}>Tags</Typography>
              <Stack direction="row" alignItems="flex-start" gap={0.75}>
                <Box sx={{flex: 1, minWidth: 0}}>
                  <MultiSelectAutocomplete
                    options={project?.settings?.tags || []}
                    selectedValues={node?.routes?.dynamic[index]?.data?.tags || null}
                    placeholder="Select a tag..."
                    getOptionLabel={(opt) => opt.name}
                    getOptionValue={(opt) => opt.id}
                    size="small"
                    multiple
                    blurOnSelect={false}
                    onChange={(value) => {
                      setNode(prev => {
                        const newNode = {...prev};
                        let next = {...newNode.routes?.dynamic[index]};
                        if (!next) {
                          next = createRoute();
                        }
                        const data = {...(next?.data || {})};
                        data.tags = value;

                        next.id = next?.id || uuidv4();
                        next.data = data;
                        newNode.routes.dynamic[index] = next;
                        return newNode;
                      });
                    }}
                    onBlur={() => onUpdate(node)}
                  />
                </Box>
                <Tooltip title="Manage tags" disableInteractive>
                  <IconButton onClick={() => setManageTagsOpen(true)} sx={iconBtnSx}>
                    <HugeiconsIcon icon={Settings01Icon} size={17}/>
                  </IconButton>
                </Tooltip>
              </Stack>
            </Box>

            <TextField
              label="First Command"
              size="small"
              fullWidth
              sx={fieldSx}
              value={routeData?.data?.first_command || ""}
              onChange={(e) => {
                setNode(prev => {
                  const newNode = {...prev};
                  let next = {...newNode.routes?.dynamic[index]};
                  if (!next) {
                    next = createRoute();
                  }
                  const data = {...next.data};
                  data.first_command = e.target.value;

                  next.id = next?.id || uuidv4();
                  next.data = data;
                  newNode.routes.dynamic[index] = next;
                  return newNode;
                });
              }}
              onBlur={() => onUpdate(node)}
            />

            <TextField
              label="Priority"
              size="small"
              fullWidth
              type="number"
              sx={fieldSx}
              value={routeData?.data?.priority || ""}
              onChange={(e) => {
                setNode(prev => {
                  const newNode = {...prev};
                  let next = {...newNode.routes?.dynamic[index]};
                  if (!next) {
                    next = createRoute();
                  }
                  const data = {...next.data};
                  data.priority = e.target.value ? +e.target.value : "";

                  next.id = next?.id || uuidv4();
                  next.data = data;
                  newNode.routes.dynamic[index] = next;
                  return newNode;
                });
              }}
              onBlur={() => onUpdate(node)}
            />

            <FormControlLabel
              sx={{ml: -0.5, mr: 0}}
              control={
                <Checkbox
                  size="small"
                  sx={checkboxSx}
                  onChange={(e, value) => {
                    setNode(prev => {
                      const newNode = {...prev};
                      let next = {...newNode.routes?.dynamic[index]};
                      if (!next) {
                        next = createRoute();
                      }
                      next.id = next?.id || uuidv4();
                      next.is_enabled_cbw = value;
                      next.conversation_buffer_window = 0;
                      newNode.routes.dynamic[index] = next;

                      return newNode;
                    });
                  }}
                  checked={routeData?.is_enabled_cbw || false}
                />
              }
              label={<Typography sx={controlLabelSx}>Enable Conversation Buffer Window</Typography>}
            />

            {routeData?.is_enabled_cbw ? (
              <TextField
                label="Conversation Buffer Window"
                size="small"
                type="number"
                fullWidth
                sx={fieldSx}
                value={routeData?.data?.conversation_buffer_window || ""}
                onChange={(e) => {
                  setNode(prev => {
                    const newNode = {...prev};
                    let next = {...newNode.routes?.dynamic[index]};
                    if (!next) {
                      next = createRoute();
                    }
                    const data = {...next.data};
                    data.conversation_buffer_window = e.target.value;

                    next.id = next?.id || uuidv4();
                    next.data = data;
                    newNode.routes.dynamic[index] = next;
                    return newNode;
                  });
                }}
                onBlur={() => onUpdate(node)}
              />
            ) : null}

            <FormControlLabel
              sx={{ml: -0.5, mr: 0}}
              control={
                <Checkbox
                  size="small"
                  sx={checkboxSx}
                  onChange={(e, value) => {
                    setNode(prev => {
                      const newNode = {...prev};
                      let next = {...newNode.routes?.dynamic[index]};
                      if (!next) {
                        next = createRoute();
                      }
                      next.id = next?.id || uuidv4();
                      next.backstack = value;
                      newNode.routes.dynamic[index] = next;

                      return newNode;
                    });
                  }}
                  checked={routeData?.backstack || false}
                />
              }
              label={<Typography sx={controlLabelSx}>Backstack</Typography>}
            />

            {/* SET VARS */}
            <Box>
              <Typography sx={{...sectionLabelSx, mb: varsInfo.secondary ? 0.25 : 0.75}}>
                {varsInfo.primary || "Set variables"}
              </Typography>
              {varsInfo.secondary && (
                <Typography sx={{mb: 0.75, fontSize: 11, color: UI.muted, lineHeight: 1.35}}>
                  {varsInfo.secondary}
                </Typography>
              )}
              <Box sx={{border: `1px solid ${rgba(UI.ink, 0.1)}`, borderRadius: "10px", overflow: "hidden"}}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{"& th": {borderColor: rgba(UI.ink, 0.08), backgroundColor: rgba(UI.accent, 0.04)}}}>
                      <TableCell sx={{...sectionLabelSx, py: 0.75, px: 1}}>Target</TableCell>
                      <TableCell sx={{...sectionLabelSx, py: 0.75, px: 1}}>From</TableCell>
                      <TableCell sx={{py: 0.5, px: 0.5, width: 36}}>
                        <Tooltip title="Add new variable" disableInteractive>
                          <IconButton size="small" onClick={() => setOpen(route?.id)} sx={iconBtnSx}>
                            <HugeiconsIcon icon={Add01Icon} size={16}/>
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {route?.data?.set_vars?.map((varParam, i) => (
                      <TableRow key={`${varParam.source_scope}-${i}`} sx={{"& td": {borderColor: rgba(UI.ink, 0.06)}}}>
                        <TableCell sx={{fontSize: 11.5, py: 0.75, px: 1, color: UI.ink}}>
                          ({varParam?.target_scope}) {varParam?.target}
                        </TableCell>
                        <TableCell sx={{fontSize: 11.5, py: 0.75, px: 1, color: UI.ink}}>
                          ({varParam?.source_scope}) {varParam?.source}
                        </TableCell>
                        <TableCell sx={{py: 0.5, px: 0.5}}>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSelectedVar(i);
                              setOpen(route?.id);
                            }}
                            sx={iconBtnSx}
                          >
                            <HugeiconsIcon icon={PencilEdit01Icon} size={16}/>
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Box>

            <RouteParamsList
              params={routeData?.data?.params || []}
              onAdd={() => {
                setNode(prev => {
                  const newNode = {...prev};
                  let next = {...newNode.routes.dynamic[index]};
                  if (!next) {
                    next = createRoute(route);
                  }
                  const data = {...next.data};
                  data.params = [...data?.params || [], ""];
                  next.id = next?.id || uuidv4();
                  next.data = data;
                  newNode.routes.dynamic[index] = next;
                  return newNode;
                });
              }}
              onChange={(ind, val) => {
                setNode(prev => {
                  const newNode = {...prev};
                  let next = {...newNode.routes.dynamic[index]};
                  if (!next) {
                    next = createRoute();
                  }
                  const data = {...next.data};
                  data.params[ind] = val;

                  next.id = next?.id || uuidv4();
                  next.data = data;
                  newNode.routes.dynamic[index] = next;
                  if (newNode?.routes?.dynamic[index] !== node?.routes?.dynamic[index]) {
                    onUpdate(newNode);
                  }
                  return newNode;
                });
              }}
              onRemove={(ind) => {
                setNode(prev => {
                  const newNode = {...prev};
                  let next = {...newNode.routes.dynamic[index]};
                  if (!next) {
                    next = createRoute();
                  }
                  const data = {...next.data};
                  data.params = [...(data?.params || [])];
                  data.params = data?.params?.filter((p, i) => i !== ind);

                  next.id = next?.id || uuidv4();
                  next.data = data;
                  newNode.routes.dynamic[index] = next;
                  if (newNode?.routes?.dynamic[index] !== node?.routes?.dynamic[index]) {
                    onUpdate(newNode);
                  }
                  return newNode;
                });
              }}
            />

            <Button
              variant="outlined"
              color="error"
              size="small"
              fullWidth
              sx={{textTransform: "none", borderRadius: "8px"}}
              onClick={() => onDeleteRoute(route?.id)}
            >
              Delete Route
            </Button>
          </RouteSection>
        );
      })}
      </Stack>
    </>
  );
};
